import { atom, selector, useRecoilValue, useSetRecoilState } from 'recoil';
import { useEffectOnce } from 'common/hooks';
import { TBasketItem } from '../types';

type TState = {
  basket: TBasketItem[] | null;
};

const BASKET_LOAD = 'basket:load';
const BASKET_UPDATE = 'basket:update';
const BASKET_ADD_PRODUCTS = 'basket:add_products';

const DEFAULT_STATE: TState = {
  basket: null,
};

const basketState = atom({
  key: 'basketState',
  default: DEFAULT_STATE,
});

const basketIsLoadingState = selector({
  key: 'basketIsLoadingState',
  get: ({ get }) => {
    const { basket } = get(basketState);
    return basket === null;
  },
});

export const useBasketState = () => {
  const setBasketState = useSetRecoilState(basketState);
  const isBasketLoading = useRecoilValue(basketIsLoadingState);
  const { basket } = useRecoilValue(basketState);

  const loadBasket = () => {
    document.body.dispatchEvent(new CustomEvent(BASKET_LOAD));
  };

  const updateBasket = (items: TBasketItem[]) => {
    setBasketState(prev => ({ ...prev, basket: items }));
  };

  const addProductsToBasket = (items: TBasketItem[]) => {
    document.body.dispatchEvent(new CustomEvent(BASKET_ADD_PRODUCTS, { detail: items }));
  };

  return {
    basket: basket ?? [],
    isBasketLoading,
    loadBasket,
    updateBasket,
    addProductsToBasket,
  };
};

const isBasketUpdateEvent = (e: Event): e is CustomEvent<TBasketItem[]> =>
  'detail' in e && Array.isArray((e as CustomEvent).detail);

export const useBasketUpdate = (load: () => void, update: (basket: TBasketItem[]) => void): void => {
  useEffectOnce(() => {
    const handleBasketUpdate = (e: Event) => {
      if (isBasketUpdateEvent(e)) {
        update(e.detail);
      }
    };

    document.body.addEventListener(BASKET_UPDATE, handleBasketUpdate);
    load();

    return () => {
      document.body.removeEventListener(BASKET_UPDATE, handleBasketUpdate);
    };
  });
};
